import React, {
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
  useRef
} from 'react'
import { useSelector } from 'react-redux'

import shortid from 'shortid';

import { WidgetsContext, GatingContext, ModalContext } from './../core/context'
import { Widgets } from './../core'
import { MODAL, SLIDE } from '../../../constants/widgetTypes'
import {
  useFormSuccessSelector,
  useOtpFormSuccessSelector,
  useOtpRequiredSelector,
  usePageDuration, usePagePercentage,
} from '../core/hooks'
import { Fonts } from '@paperflite-marketing/cleverstory-commons'
import { SpatialPage } from '../spatialContent'
import { CarouselContext } from '../InteractiveContent';
import { isIcV2, useInteractiveContent } from '../InteractiveContent/v2';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';

/* 
  Interactive Content version two will have Individual GatingManager Instance for each pages via PageWithContext component
  in the Viewer repo. To prevent multiple gating for same page, we will use managerId to identify the gating manager. 
*/
const shouldTriggerIcV2Gating = (storyboardType, carouselContext, managerId) => {
  return isIcV2(storyboardType) && 
         carouselContext.triggeredGating && 
         carouselContext.triggeredGating.managerId !== managerId;
};

const acquireIcV2GatingLock = (storyboardType, carouselContext, managerId, gating) => {
  if (isIcV2(storyboardType) && carouselContext.setTriggeredGating) {
    carouselContext.setTriggeredGating({
      managerId,
      gating
    });
  }
};

const releaseIcV2GatingLock = (storyboardType, carouselContext) => {
  if (isIcV2(storyboardType) && carouselContext.setTriggeredGating) {
    carouselContext.setTriggeredGating(null);
  }
};


export const GatingManager = React.forwardRef(
  (
    {
      configurations,
      children,
      resetGating,
      page,
      breakpoint,
      appearance,
      storyboardType,
      preview,
      onGatingClose, 
      triggerGatingInteraction, 
      pageSession, 
      spatialSmartGating,
      progressiveGatingTrigger,
      updateFormStatus },
    ref
  ) => {
    const context = React.useContext(WidgetsContext)
    const carouselContext = React.useContext(CarouselContext)
    const managerRef = useRef(shortid.generate())

    const [gatingContext, setGatingContext] = React.useState({
      preview: preview,
      disabled: false,
      triggerGating: () => {},
      type: null,
      payload: null,
      gating: null,
      configuration: null,
      configurations: [],
      assetConfigurations: [],
      disabledAssetDownload: true
    })

    const [widgetContextState, setWidgetContextState] = React.useState({
      ...context,
      eventUid: '',
      gatingTrigger: null,
      smart: false
    })
    const { storyboardLevelAlias = null} = useInteractiveContent() || {}

    const configurationsRef = useRef([]);
    const triggeredGatingsRef = useRef(new Set());
    const submittedNonProgressiveGatingsRef = useRef(new Set());
    const [shouldDelayClose, setShouldDelayClose] = useState(false);

    const currentGatingId = gatingContext.gating;
    const currentConfiguration = configurations?.find(g => 
      get(g, 'gating.id') === currentGatingId
    );
    const cluster = get(currentConfiguration, 'cluster', null);
    const clusterLoading = get(currentConfiguration, 'clusterLoading', false);
    const clusterFields = get(cluster, 'fields', []);
    var formSuccess = useFormSuccessSelector(widgetContextState.selector)
    var otpFormSuccess = useOtpFormSuccessSelector(widgetContextState.selector)
    var otpRequired = useOtpRequiredSelector(widgetContextState.selector)
    const activeSlide = useSelector((state) =>
      get(state, `pages.spatial.activePage`, null)
    )
    const spatialPages = useSelector(state => get(state, 'pages.spatial.items', []));
    const activeSpatialPage = spatialPages.find(page => page.id === activeSlide);
    const eventUidRef = useRef('');

    const [exitIntentTriggered, setExitIntentTriggered] = useState(false);
    const exitIntentListenerRef = useRef(null);
    const resolvedAlias = isIcV2(storyboardType) ? storyboardLevelAlias : page

    const duration = usePageDuration(resolvedAlias, storyboardType)
    const percentage = usePagePercentage(resolvedAlias, storyboardType);

    const smartSpatialDuration = usePageDuration(resolvedAlias, storyboardType, true);
    const smartSpatialPercentage = usePagePercentage(resolvedAlias, storyboardType, true);

    const assetType =
      get(gatingContext, 'configuration.target.entityType', '') === 'asset'

    useEffect(() => {
      configurationsRef.current = gatingContext.configurations;
    }, [gatingContext.configurations]);

    useEffect(() => {
      setGatingContext((gatingContext) => {
        return {
          ...gatingContext,
          triggerGating: triggerGating
        }
      })
    }, [])

    React.useEffect(() => {
      if (gatingContext.gating) {
        const targetEntityType = get(gatingContext.configuration, 'target.entityType', '');
        const isAssetTarget = targetEntityType === 'asset';
        const isProgressive = isAssetTarget ? false : get(gatingContext.configuration, 'progressive', false);
        if (preview) {
          setWidgetContextState((widgetContextState) => {
            return {
              ...widgetContextState,
              ...context.createGatingContext(gatingContext.gating),
              progressiveGating: {
                enabled: false,
                clusterFields: [],
                clusterId: null,
                clusterOrder: 1,
                isLoading: false
              }
            }
          });
          return;
        }
    
        if (isProgressive && !clusterLoading) {
          if (!clusterFields || clusterFields.length === 0) {
            return;
          }
          
          setWidgetContextState((widgetContextState) => {
            const newProgressiveGating = {
              enabled: true,
              clusterFields: clusterFields,
              clusterId: cluster?.id || null,
              clusterOrder: cluster?.order || 1,
              isLoading: clusterLoading
            };
            
            const currentGatingSelector = widgetContextState.selector;
            const newGatingContext = context.createGatingContext(gatingContext.gating);
            const newGatingSelector = newGatingContext.selector;
            
            if (isEqual(widgetContextState.progressiveGating, newProgressiveGating) && 
                currentGatingSelector === newGatingSelector) {
              return widgetContextState;
            }
            
            return {
              ...widgetContextState,
              ...newGatingContext,
              progressiveGating: newProgressiveGating
            };
          });
        } else if (!isProgressive) {
          setWidgetContextState((widgetContextState) => {
            const newProgressiveGating = {
              enabled: false,
              clusterFields: [],
              clusterId: null,
              clusterOrder: 1,
              isLoading: false
            };
    
            const currentGatingSelector = widgetContextState.selector;
            const newGatingContext = context.createGatingContext(gatingContext.gating);
            const newGatingSelector = newGatingContext.selector;
            
            if (isEqual(widgetContextState.progressiveGating, newProgressiveGating) && 
                currentGatingSelector === newGatingSelector) {
              return widgetContextState;
            }
            
            return {
              ...widgetContextState,
              ...newGatingContext,
              progressiveGating: newProgressiveGating
            }
          });
        }
      }
    }, [gatingContext.gating, clusterLoading, preview]);

    useEffect(() => {
      if (!gatingContext.gating || preview || clusterLoading) {
        return;
      }
    
      const targetEntityType = get(gatingContext.configuration, 'target.entityType', '');
      const isAssetTarget = targetEntityType === 'asset';
      const isProgressive = isAssetTarget ? false : get(gatingContext.configuration, 'progressive', false);
    
      if (!isProgressive) {
        return;
      }
    
      const liveConfiguration = configurations?.find(g =>
        get(g, 'gating.id') === gatingContext.gating
      );
    
      if (!liveConfiguration) {
        setGatingContext((prevGatingContext) => {
          const updatedConfigurations = (prevGatingContext.configurations || []).filter(item => {
            const itemTargetEntityType = get(item, 'target.entityType', '');
            const itemIsAssetTarget = itemTargetEntityType === 'asset';
            const itemIsProgressive = itemIsAssetTarget ? false : get(item, 'progressive', false);
            return !itemIsProgressive;
          });
    
          configurationsRef.current = updatedConfigurations;
    
          return {
            ...prevGatingContext,
            gating: null,
            configuration: null,
            configurations: updatedConfigurations,
            disabled: updatedConfigurations.length === 0
          };
        });
      }
    }, [configurations, clusterLoading]);

    useEffect(() => {
      initiateGatingForTimeSpent()
    }, [duration, smartSpatialDuration])

    useEffect(() => {
      setupExitIntentListener()
      return () => {
        cleanupExitIntentListener()
      }
    }, [gatingContext.configurations, duration, gatingContext.gating, gatingContext.disabled, preview])

    useEffect(() => {
      setGatingContext((prevGatingContext) => {
        if (isEqual(prevGatingContext.configurations, configurations)) {
          return prevGatingContext;
        }
        return {
          ...prevGatingContext,
          configurations: configurations
        }
      })
    }, [configurations])

    useEffect(() => {
      if (formSuccess && !otpRequired) {
        setShouldDelayClose(true);
        
        setTimeout(() => {
          handleFormSuccess();
          setShouldDelayClose(false);
        }, 2000);
      }
    }, [formSuccess, otpRequired])

    useEffect(() => {
      if (otpFormSuccess && otpRequired) {
        setShouldDelayClose(true);
        
        setTimeout(() => {
          handleFormSuccess();
          setShouldDelayClose(false);
        }, 2000);
      }
    }, [otpFormSuccess, otpRequired])

    useEffect(() => {
      if (context) {
        setWidgetContextState((prevWidgetContextState) => {
          const newState = {
            ...context,
            ...prevWidgetContextState
          };
          
          if (isEqual(prevWidgetContextState, newState)) {
            return prevWidgetContextState;
          }
          
          return newState;
        })
      }
    }, [context.session, context.selector]);

    React.useEffect(() => {
      if (
        isIcV2(storyboardType) && 
        gatingContext.gating && 
        shouldTriggerIcV2Gating(storyboardType, carouselContext, managerRef.current)
      ) {
        setGatingContext((gatingContext) => {
          return {
            ...gatingContext,
            gating: null,
            configuration: null,
            configurations: []
          }
        })
      }
    }, [carouselContext.triggeredGating])

    React.useImperativeHandle(ref, () => ({
      triggerGating: triggerGating,
      closeGating: handleClose,
      gatingContext,
    }))

    const triggerGating = (type, payload, configuration) => {
      if (!configuration) return;

      const targetEntityType = get(configuration, 'target.entityType', '');
      const isAssetTarget = targetEntityType === 'asset';
      const isProgressive = isAssetTarget ? false : get(configuration, 'progressive', false);

      const newGatingId = get(configuration, 'gating.id');
      const currentGatingId = gatingContext.gating;
      const configId = get(configuration, 'id', null);
      const gatingId = get(configuration, 'gating.id', null);
    
      if (gatingContext.disabled) {
        return;
      }
    
      if (storyboardType === 'SPATIAL' && type === 'PREVIEW' && activeSlide !== page) {
        return;
      }
    
      if (currentGatingId && currentGatingId !== newGatingId && !isProgressive) {
        return;
      }
    
      if (currentGatingId === newGatingId && !isProgressive) {
        return;
      }
    
      if (shouldTriggerIcV2Gating(storyboardType, carouselContext, managerRef.current)) {
        return
      }

      acquireIcV2GatingLock(storyboardType, carouselContext, managerRef.current, get(configuration, 'gating.id'));

      if (configId && triggeredGatingsRef.current.has(configId)) {
        return;
      }

      if (!isProgressive && gatingId && submittedNonProgressiveGatingsRef.current.has(gatingId)) {
        return;
      }
    
      var gating = newGatingId;
    
      const trigger = get(configuration, 'trigger', null);
    
      if (isProgressive) {
        if (progressiveGatingTrigger && gating && trigger) {
          progressiveGatingTrigger(gating, trigger);
        }
      }
    
      if (configId) {
        triggeredGatingsRef.current.add(configId);
      }
    
      setGatingContext((gatingContext) => {
        return {
          ...gatingContext,
          gating: gating,
          type: type,
          payload: payload,
          configuration: configuration
        }
      })
    }


    
    const initiateGatingForTimeSpent = () => {
      if (gatingContext.gating || gatingContext.disabled || preview || isEmpty(configurationsRef.current)) {
        return
      }

      var gatingConfigurations = configurationsRef.current?.filter((item) => {
        const configId = get(item, 'id', null);
        if (triggeredGatingsRef.current.has(configId)) {
          return false;
        }
        const gatingId = get(item, 'gating.id', null);
        const itemTargetEntityType = get(item, 'target.entityType', '');
        const itemIsAssetTarget = itemTargetEntityType === 'asset';
        const itemIsProgressive = itemIsAssetTarget ? false : get(item, 'progressive', false);
        
        if (!itemIsProgressive && gatingId && submittedNonProgressiveGatingsRef.current.has(gatingId)) {
          return false;
        }
        return get(item, 'trigger.type', null) === 'TIME' || get(item, 'trigger.type', null) === 'PERCENTAGE'
      })

      gatingConfigurations.every((item) => {
        var triggerType = get(item, 'trigger.type', null);
        var triggerValue = get(item, 'trigger.value', 0);
        var isSmartGating = get(item, 'smart', false) == true;
        const targetValue = get(item, 'target', null);
        const isSmartSpatialGating = storyboardType === 'SPATIAL' && isSmartGating;
        const conditionalDuration = isSmartSpatialGating ? smartSpatialDuration : duration;
        const conditionalPercentage = isSmartSpatialGating ? smartSpatialPercentage: percentage;

        if (isSmartSpatialGating) {
          set(item, 'target', activeSpatialPage || targetValue);
        }

        if ((triggerType === 'TIME' && triggerValue <= conditionalDuration) || (triggerType === 'PERCENTAGE' && triggerValue <= conditionalPercentage)) {
          var gating = get(item, 'gating.id', null)
          var target = get(item, 'target.id', null)
          var targetCanonical = get(item, 'target.canonical', null)

          const isValidGating = storyboardType === 'SPATIAL' ? gating && target === activeSlide && page === targetCanonical  : gating;

          if(isValidGating) {
            const configId = get(item, 'id', null);
            triggeredGatingsRef.current.add(configId);
                        
            
            if (shouldTriggerIcV2Gating(storyboardType, carouselContext, managerRef.current)) {
              return false
            }

            acquireIcV2GatingLock(storyboardType, carouselContext, managerRef.current, gating);

            if (isSmartSpatialGating) {
              spatialSmartGating(configId)
            }

            const targetEntityType = get(item, 'target.entityType', '');
            const isAssetTarget = targetEntityType === 'asset';
            const isProgressive = isAssetTarget ? false : get(item, 'progressive', false);
    
            const trigger = get(item, 'trigger', null);
        
            if (isProgressive && progressiveGatingTrigger && gating && trigger) {
              progressiveGatingTrigger(gating, trigger);
            }
            setGatingContext((gatingContext) => {
              return {
                ...gatingContext,
                gating: gating,
                type: get(item, 'trigger.type', null),
                payload: null,
                configuration: item
              }
            })

            return false
          }
        }

        return true
      })
    }

    const setupExitIntentListener = () => {
      cleanupExitIntentListener();
      
      if (gatingContext.gating || gatingContext.disabled || preview || isEmpty(configurationsRef.current) || duration < 3) {
        return
      }

      const exitIntentConfigurations = configurationsRef.current.filter((item) => {
        const configId = get(item, 'id', null);
        const gatingId = get(item, 'gating.id', null);
        const itemTargetEntityType = get(item, 'target.entityType', '');
        const itemIsAssetTarget = itemTargetEntityType === 'asset';
        const itemIsProgressive = itemIsAssetTarget ? false : get(item, 'progressive', false);

        if (triggeredGatingsRef.current.has(configId)) {
          return false;
        }        
        if (!itemIsProgressive && gatingId && submittedNonProgressiveGatingsRef.current.has(gatingId)) {
          return false;
        }
        return get(item, 'trigger.type', null) === 'EXIT_INTENT'
      })

      if (exitIntentConfigurations.length === 0) {
        return
      }
    
      let mouseY = 0;
      let mouseVelocityY = 0;
      let lastMouseY = 0;
      let lastTime = Date.now();
    
      const mouseMoveHandler = (e) => {
        const currentTime = Date.now();
        const timeDelta = currentTime - lastTime;
        
        if (timeDelta > 0) {
          mouseVelocityY = (e.clientY - lastMouseY) / timeDelta;
        }
        
        mouseY = e.clientY;
        lastMouseY = e.clientY;
        lastTime = currentTime;
      }
    
      const exitIntentHandler = (e) => {
        const isMovingUp = mouseVelocityY < -0.5;
        const isNearTop = e.clientY <= 15;
        const isLeavingViewport = e.type === 'mouseleave' && mouseY <= 20;
    
        if ((isNearTop && isMovingUp) || (e.clientY <= 0 || e.screenY <= 3 || e.pageY <= 0) || isLeavingViewport) {
          if (!exitIntentTriggered && duration >= 3) {
            handleExitIntent(e, exitIntentConfigurations);
          }
        }
      }
    
      exitIntentListenerRef.current = {
        mouseMoveHandler: mouseMoveHandler,
        exitIntentHandler: exitIntentHandler
      };
    
      document.addEventListener('mousemove', mouseMoveHandler, { passive: true });
      document.addEventListener('mouseleave', exitIntentHandler, { passive: true });
      document.addEventListener('mouseout', exitIntentHandler, { passive: true });
      window.addEventListener('mouseout', exitIntentHandler, { passive: true });
    }

    const cleanupExitIntentListener = () => {
      if (exitIntentListenerRef.current) {
        const { mouseMoveHandler, exitIntentHandler } = exitIntentListenerRef.current;
        
        document.removeEventListener('mousemove', mouseMoveHandler);
        document.removeEventListener('mouseleave', exitIntentHandler);
        document.removeEventListener('mouseout', exitIntentHandler);
        window.removeEventListener('mouseout', exitIntentHandler);
        
        exitIntentListenerRef.current = null;
      }
    }

    const handleExitIntent = (e, exitIntentConfigurations) => {
      if (exitIntentTriggered || duration < 3) return;

    
      setExitIntentTriggered(true);
      setTimeout(() => {
        exitIntentConfigurations.every((item) => {
          var isSmartGating = get(item, 'smart', false) == true;
          const targetValue = get(item, 'target', null);
          const isSmartSpatialGating = storyboardType === 'SPATIAL' && isSmartGating;
          if (isSmartSpatialGating) {
            set(item, 'target', activeSpatialPage || targetValue);
          }
          const conditionalDuration = isSmartSpatialGating ? smartSpatialDuration : duration;
          var gating = get(item, 'gating.id', null)
          var target = get(item, 'target.id', null)
          var targetCanonical = get(item, 'target.canonical', null)

          const isValidGating = storyboardType === 'SPATIAL' ? 
            gating && target === activeSlide && page === targetCanonical : 
            gating;
    
          if (isValidGating && conditionalDuration > 3) {

            const configId = get(item, 'id', null);
            triggeredGatingsRef.current.add(configId);

            const targetEntityType = get(item, 'target.entityType', '');
            const isAssetTarget = targetEntityType === 'asset';
            const isProgressive = isAssetTarget ? false : get(item, 'progressive', false);

            const trigger = get(item, 'trigger', null);
    
            if (isProgressive && progressiveGatingTrigger && gating && trigger) {
              progressiveGatingTrigger(gating, trigger);
            }
    
            setGatingContext((gatingContext) => {
              return {
                ...gatingContext,
                gating: gating,
                type: 'EXIT_INTENT',
                payload: null,
                configuration: item
              }
            })

            if (shouldTriggerIcV2Gating(storyboardType, carouselContext, managerRef.current)) {
              return false
            }

            acquireIcV2GatingLock(storyboardType, carouselContext, managerRef.current, gating);
    
            cleanupExitIntentListener()
            return false
          }

          return true
        })
      }, 50);
    }

    const isFullScreenVideo = () => {
      const fullscreenContainer = document.fullscreenElement
      const videoElement = fullscreenContainer
        ? fullscreenContainer.querySelector('video')
        : null
      const modalElement =
        document.getElementsByClassName('ReactModalPortal')[0]
      if (videoElement) {
        fullscreenContainer.removeChild(modalElement)
      }
    }

  const handleTriggerGatingInteraction=(type)=>{
    if(preview) {
      return;
    }

    switch (type) {
      case 'CLOSE':
        triggerGatingInteraction({
          gating: gatingContext.gating,
          gatingConfiguration: get(gatingContext.configuration, "id", null),
          gatingTrigger: get(gatingContext.configuration, 'trigger', null),
          modalEvent: "MODAL_CLOSED",
          name: "Gating Closed",
          eventUid: eventUidRef.current
        })
        break;

      case 'OPEN':
        const uid = `form_${shortid.generate()}`;
        eventUidRef.current = uid; 
        setWidgetContextState(prevState => ({
          ...prevState,
          eventUid: uid,
          gatingTrigger: get(gatingContext.configuration, 'trigger', null),
          smart: get(gatingContext.configuration, 'smart', false)
        }));   
        triggerGatingInteraction({
          gating: gatingContext.gating,
          gatingConfiguration: get(gatingContext.configuration, "id", null),
          gatingTrigger: get(gatingContext.configuration, 'trigger', null),
          modalEvent: "MODAL_OPENED",
          name: "Gating Opened",
          eventUid: uid
        })
        break;
    }
  }

    const handleFormSuccess = () => {
      if (!preview) {
        if (gatingContext.type === 'INTERACTIVE_DOWNLOAD') {
          if (gatingContext.payload.download) {
            gatingContext.payload.download()
          }
        }
      }
      
      const currentConfigId = get(gatingContext.configuration, 'id', null);
      const currentGatingId = get(gatingContext.configuration, 'gating.id', null);
      const targetEntityType = get(gatingContext.configuration, 'target.entityType', '');
      const isAssetTarget = targetEntityType === 'asset';
      const isProgressive = isAssetTarget ? false : get(gatingContext.configuration, 'progressive', false);

      if (!isProgressive && currentGatingId) {
        submittedNonProgressiveGatingsRef.current.add(currentGatingId);
      }
    
      if (!preview && pageSession && context.fetchGatingCluster) {
        const progressiveGating = (gatingContext.configurations || []).find(config => {
          const configTargetEntityType = get(config, 'target.entityType', '');
          const configIsAssetTarget = configTargetEntityType === 'asset';
          const configIsProgressive = configIsAssetTarget ? false : get(config, 'progressive', false);
          return configIsProgressive;
        });
    
        if (progressiveGating) {
          const progressiveGatingId = get(progressiveGating, 'gating.id');
          if (progressiveGatingId) {
            updateFormStatus(widgetContextState.session);
            context.fetchGatingCluster({
              id: progressiveGatingId,
              gating: progressiveGatingId,
              alias: isIcV2(storyboardType) ? storyboardLevelAlias : page
            });
          }
        }
      }
      
      isFullScreenVideo()
      resetGating(page, false)
      cleanupExitIntentListener()
      handleTriggerGatingInteraction('CLOSE')
      
      setGatingContext((prevGatingContext) => {
        if (preview) {
          return {
            ...prevGatingContext,
            gating: null,
            configuration: null
          }
        }

        let updatedConfigurations;
        
        if (isProgressive) {
          updatedConfigurations = (prevGatingContext.configurations || []).filter(
            item => item.id !== currentConfigId
          );
        } else {
          updatedConfigurations = (prevGatingContext.configurations || []).filter(item => {
            const itemTargetEntityType = get(item, 'target.entityType', '');
            const itemIsAssetTarget = itemTargetEntityType === 'asset';
            const itemIsProgressive = itemIsAssetTarget ? false : get(item, 'progressive', false);
            return itemIsProgressive;
          });
        }
    
        const hasRemainingProgressiveGatings = updatedConfigurations.some(config => {
          const configTargetEntityType = get(config, 'target.entityType', '');
          const configIsAssetTarget = configTargetEntityType === 'asset';
          return !configIsAssetTarget && get(config, 'progressive', false);
        });
    
        if (!isProgressive && hasRemainingProgressiveGatings && !preview && pageSession && widgetContextState?.session) {
          updateFormStatus(widgetContextState.session);
        }
    
        const token = window.localStorage.getItem('cs_token');
        const hasToken = token && token !== 'undefined' && token !== 'null';

        configurationsRef.current = updatedConfigurations;

        return {
          ...prevGatingContext,
          triggerGating: isProgressive ? triggerGating : () => {},
          type: null,
          payload: null,
          gating: null,
          configuration: null,
          configurations: updatedConfigurations,
          disabled: !hasRemainingProgressiveGatings,
          disabledAssetDownload: !hasToken
        }
      })
      
      handleTriggerGatingInteraction('CLOSE');

      releaseIcV2GatingLock(storyboardType, carouselContext);
    }

    const handleClose = (configuration, switchGating = false) => {
      if(!configuration) {
        return;
      }
      
      const configId = get(configuration, 'id', null);
      
      if (configId) {
        triggeredGatingsRef.current.add(configId);
      }
      
      const targetEntityType = get(configuration, 'target.entityType', '');
      const isAssetTarget = targetEntityType === 'asset';
      const isProgressive = isAssetTarget ? false : get(configuration, 'progressive', false);
      
      if (isProgressive && pageSession && widgetContextState?.session) {
        updateFormStatus(widgetContextState.session);
      }
      
      var items = [...gatingContext.assetConfigurations, configuration.id]

      const filteredConfigurations = (gatingContext.configurations || []).filter(
        item => item.id !== configId
      );
      configurationsRef.current = filteredConfigurations;

    setGatingContext((gatingContext) => {
      return {
        ...gatingContext,
        gating: null,
        type: null,
        payload: null,
        configuration: null,
        configurations: filteredConfigurations,
        assetConfigurations: items,
        disabled: false
      }
    });

    isFullScreenVideo();
    resetGating(page, switchGating);

    releaseIcV2GatingLock(storyboardType, carouselContext);

    handleTriggerGatingInteraction('CLOSE');

    cleanupExitIntentListener()

    if(assetType && !configuration?.optional) {
      onGatingClose();
    }
  }

  return (
    <GatingContext.Provider ref={ref} value={gatingContext}>
    {(() => {
      const targetEntityType = get(gatingContext.configuration, 'target.entityType', '');
      const isAssetTarget = targetEntityType === 'asset';
      const isProgressive = isAssetTarget ? false : get(gatingContext.configuration, 'progressive', false);
      
      const hasClusterFields = clusterFields && clusterFields.length > 0;
      const shouldBlockRender = (isProgressive && !hasClusterFields) || clusterLoading;
      
      const shouldRender = gatingContext.gating && ((pageSession && !shouldBlockRender) || preview);  

      return shouldRender ? (
        <GatingRender
          widgetContext={widgetContextState}
          appearance={appearance}
          configuration={gatingContext.configuration}
          gatingId={gatingContext.gating}
          show={gatingContext.gating !== null}
          onClose={handleClose}
          handleTriggerGatingInteraction={handleTriggerGatingInteraction}
          breakpoint={breakpoint}
          assetType={assetType}
          preview={preview}
          shouldDelayClose={shouldDelayClose}
        />
      ) : null;
    })()}
    {children}
  </GatingContext.Provider>
  )
  
  }
)

const GatingRender = React.memo(({
  widgetContext,
  configuration,
  gatingId,
  appearance,
  show,
  onClose,
  assetType,
  breakpoint,
  preview, 
  handleTriggerGatingInteraction,
  shouldDelayClose
}) => {
  const [modalContext, setModalContext] = React.useState({})
  const gating = useSelector((state) =>
    get(state, `gatings.gating[${gatingId}].item`, null)
  )
  const activeSlide = useSelector((state) =>
    get(state, `pages.spatial.activePage`, null)
  )

  var widgets = get(gating, 'draft.widgets', [])
  if (!preview) {
    widgets = get(gating, 'content.widgets', [])

    const fonts = get(gating, `content.fonts`) || []
    Fonts.useFont(fonts)
  }

  useEffect(() => {
    if (widgetContext.onTriggerMixpanelEvent) {
      widgetContext.onTriggerMixpanelEvent(
        widgetContext.session,
        'Page Viewed',
        {
          screen_name: 'Gating Form'
        }
      )
    }

    handleTriggerGatingInteraction('OPEN')

  }, [])

  const modalWidget = widgets.find(
    (item) => item.type === MODAL || item.type === SLIDE
  )
  const assetModal = get(configuration, 'target.assetModal', null)



  useEffect(() => {
    if (configuration?.optional || (assetType && assetModal)) {
      setModalContext((modalContext) => {
        return {
          ...modalContext,
          close: handleCloseGating
        }
      })
    }
  }, [configuration, assetType, assetModal])

  const handleCloseGating = () => {

    if (shouldDelayClose) {
      return;
    }
    
    if (onClose) {
      onClose(configuration)
    }
  }

  if (!modalWidget) {
    return null
  }

  return (
    <WidgetsContext.Provider value={widgetContext}>
      <ModalContext.Provider value={modalContext}>
      { modalWidget.type === SLIDE ?
        <SpatialPage preview={preview} appearance={appearance} page={{id:activeSlide}}>
          <Widgets
            key={modalWidget.uid}
            widgetProps={modalWidget}
            breakpoint={breakpoint}
            show={show}
          />
        </SpatialPage>
        :
          <Widgets
            key={modalWidget.uid}
            widgetProps={modalWidget}
            breakpoint={breakpoint}
            show={show}
          />
      }
      </ModalContext.Provider>
    </WidgetsContext.Provider>
  )
})
